import { useEffect, useState } from 'react'
import {
  Title,
  Stack,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  Switch,
  Text,
  Alert,
  Loader,
  Center,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconPencil, IconTrash, IconPlus, IconAlertCircle } from '@tabler/icons-react'
import { useApplicationsStore } from '../../stores/applicationsStore'
import { Application, CreateApplicationData, UpdateApplicationData } from '../../api/applicationsApi'
import { ApplicationForm } from './ApplicationForm'

export function ApplicationsView() {
  const { applications, isLoading, error, fetchApplications, createApplication, updateApplication, deleteApplication, clearError } = useApplicationsStore()

  const [showInactive, setShowInactive] = useState(false)
  const [formOpened, setFormOpened] = useState(false)
  const [editingApp, setEditingApp] = useState<Application | null>(null)

  useEffect(() => {
    fetchApplications(showInactive)
  }, [showInactive, fetchApplications])

  const handleCreate = () => {
    setEditingApp(null)
    setFormOpened(true)
  }

  const handleEdit = (app: Application) => {
    setEditingApp(app)
    setFormOpened(true)
  }

  const handleFormSubmit = async (data: CreateApplicationData | UpdateApplicationData): Promise<boolean> => {
    let success: boolean
    if (editingApp) {
      success = await updateApplication(editingApp.appId, data as UpdateApplicationData)
      if (success) {
        notifications.show({ title: 'Success', message: 'Application updated', color: 'green' })
      }
    } else {
      success = await createApplication(data as CreateApplicationData)
      if (success) {
        notifications.show({ title: 'Success', message: 'Application created', color: 'green' })
      }
    }
    return success
  }

  const handleDelete = (app: Application) => {
    const appId = app.appId
    const appName = app.name

    modals.openConfirmModal({
      title: 'Delete Application',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{appName}</strong>? This will deactivate the application and revoke all active sessions.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        // Verify app still exists before deleting (prevents stale closure issues)
        const currentApps = useApplicationsStore.getState().applications
        const stillExists = currentApps.some((a) => a.appId === appId)
        if (!stillExists) {
          notifications.show({ title: 'Error', message: 'Application no longer exists', color: 'red' })
          return
        }

        const success = await deleteApplication(appId)
        if (success) {
          notifications.show({ title: 'Deleted', message: `${appName} has been deleted`, color: 'green' })
        }
      },
    })
  }

  const rows = applications.map((app) => (
    <Table.Tr key={app.appId}>
      <Table.Td>{app.name}</Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed" ff="monospace">
          {app.appId}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {app.url || '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {app.passwordResetUrl || '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {app.allowedRoles?.length || 0}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge color={app.isActive ? 'green' : 'gray'} variant="light">
          {app.isActive ? 'Active' : 'Inactive'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <ActionIcon variant="subtle" onClick={() => handleEdit(app)}>
            <IconPencil size={16} />
          </ActionIcon>
          <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(app)}>
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  ))

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Applications</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
          New Application
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" onClose={clearError} withCloseButton>
          {error}
        </Alert>
      )}

      <Switch
        label="Show inactive applications"
        checked={showInactive}
        onChange={(e) => setShowInactive(e.target.checked)}
      />

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : applications.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No applications found. Create your first application to get started.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>App ID</Table.Th>
              <Table.Th>URL</Table.Th>
              <Table.Th>Password Reset URL</Table.Th>
              <Table.Th>Roles</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      )}

      <ApplicationForm
        opened={formOpened}
        onClose={() => setFormOpened(false)}
        onSubmit={handleFormSubmit}
        application={editingApp}
      />
    </Stack>
  )
}
